/**
 * PATH: packages/ui/src/components/sidebar-chrome/SidebarChrome.tsx
 * PURPOSE: Shared structural shell for every sidebar in the repo.
 *          Owns the outer <aside>, mobile drawer + backdrop, body
 *          scroll-lock while mobile is open, Cmd/Ctrl+B + Escape
 *          keyboard shortcuts, TooltipProvider wrap, and the
 *          optional floating ContextMenu portal.
 *
 * WHY: Both `TenantSidebar` (consumed by the 4 single-tenant shells
 *      — sam, newmark, steel, admin) and the master Quotech HQ
 *      `Sidebar` (consumed by 13+ hub apps via `DashboardLayout`)
 *      used to ship their own copies of the same chrome. When a
 *      UX change landed on one (e.g. collapsed-mode visuals, mobile
 *      drawer, keyboard shortcut), the other was forgotten. The
 *      operator's repeated complaint, paraphrased: "Same platform,
 *      stop double-writing." Lifting the chrome here means any
 *      future structural change happens in ONE place and propagates
 *      to all 17+ consumers.
 *
 * DESIGN: SidebarChrome does NOT render sections / items / hub
 * flyouts / customisation modals / etc. — those are CONSUMER
 * concerns and stay in the wrapper (TenantSidebar or master
 * Sidebar). Consumers supply ReactNode slots for `header`, `nav`,
 * and `footer`; SidebarChrome arranges them inside the aside with
 * the right keyboard/mobile/portal infrastructure.
 */

import { useEffect } from 'react'
import { cn } from '../../lib/utils'
import { isEditorShortcutTarget } from './sidebarChromeShortcut'
import {
  ContextMenu,
  type ContextMenuItem,
  type ContextMenuPosition,
} from '../../contextMenu'
import { TooltipProvider } from '../ui/tooltip'

export interface SidebarChromeProps {
  /** Locked collapsed state (true == icon-rail, false == expanded). */
  collapsed: boolean
  /**
   * Cmd/Ctrl+B handler. When provided, the chrome installs a
   * window-level keydown listener that invokes this on the shortcut
   * unless focus is in an editor (that chord is Bold there).
   * Optional so a read-only Storybook sample can omit it cleanly.
   */
  onToggleCollapsed?: () => void
  /**
   * Header slot. Rendered first inside the aside. The consumer
   * controls header height + dividers — chrome does NOT impose a
   * 48px header bar (tenant uses 48px; master uses 48px + a 32px
   * search-bar row).
   */
  header: React.ReactNode
  /**
   * Main nav slot. Typically the consumer renders a `<nav>` element
   * here with their own padding / scroll behaviour. Chrome does
   * NOT wrap this in `<nav>` so the consumer can attach
   * `role="navigation"` + `aria-label` + scroll refs themselves.
   */
  nav: React.ReactNode
  /**
   * Footer slot. Includes any tenant-specific footer actions
   * (Settings, Logout, branding) AND the collapse-toggle button if
   * the consumer chooses the inline pattern. Master Quotech HQ
   * uses a separate floating chevron button which it renders inside
   * this footer slot but with `position: absolute` to overhang the
   * aside's right edge.
   */
  footer: React.ReactNode
  /**
   * Optional content rendered BEFORE the header — used by master
   * Quotech HQ for its `CollapsedHubsPortal` (a portal-mounted
   * popover that React-conceptually belongs near the top of the
   * aside even though it renders via `createPortal` to `document.body`).
   */
  topPortal?: React.ReactNode
  /**
   * Position mode for the outer <aside>.
   *   - 'fixed'    → `fixed inset-y-0 left-0` (default; tenant shells
   *                  use this because their AppShell positions the
   *                  main content with `ml-56`).
   *   - 'relative' → `relative` (master Quotech HQ uses this because
   *                  it lives inside `DashboardLayout`'s layout slot
   *                  AND because its floating collapse button anchors
   *                  to the aside's bounding box).
   */
  position?: 'fixed' | 'relative'
  /** Tailwind class for the expanded-state width. Defaults to `w-56` (tenant). Master uses `w-60`. */
  expandedWidthClass?: string
  /** Tailwind class for the collapsed-state width. Defaults to `w-[4.75rem]` (76px) — standardised across tenant + master. */
  collapsedWidthClass?: string
  /**
   * Surface classes (background + border) applied to the outer <aside>.
   * Defaults to tenant's `bg-sidebar border-r border-sidebar-border`.
   * Master Quotech HQ overrides with `bg-card border-r border-border`
   * because hub apps theme through the `card` + `border` tokens.
   */
  surfaceClass?: string
  /**
   * Opt-in mobile slide-in drawer. When provided:
   *   - The aside slides off-screen below `md` by default and slides
   *     in when `open === true`.
   *   - A backdrop overlay renders behind the aside while open;
   *     clicking it (or pressing Escape) invokes `onClose`.
   *   - `document.body.style.overflow` is locked to `'hidden'`
   *     while the drawer is open to prevent background scroll on
   *     touch devices.
   * Omitted → desktop-only sidebar (master Quotech HQ never enables
   * this since its hub apps use `DashboardLayout`'s own mobile
   * handling; only the 4 tenant shells potentially use it).
   */
  mobile?: {
    open: boolean
    onClose: () => void
  }
  /**
   * Floating ContextMenu portal anchored inside the aside. The menu
   * itself uses `createPortal` so this just wires the lifecycle and
   * the host-supplied items. Optional — sidebars without right-click
   * affordances pass `undefined`.
   */
  contextMenu?: {
    open: boolean
    position: ContextMenuPosition
    items: ContextMenuItem[]
    onClose: () => void
    /** Pixel width — defaults to 240. */
    width?: number
  }
  /** Optional extra Tailwind classes appended to the outer <aside>. */
  className?: string
}

export function SidebarChrome({
  collapsed,
  onToggleCollapsed,
  header,
  nav,
  footer,
  topPortal,
  position = 'fixed',
  expandedWidthClass = 'w-56',
  collapsedWidthClass = 'w-[4.75rem]',
  surfaceClass = 'bg-sidebar border-r border-sidebar-border',
  mobile,
  contextMenu,
  className,
}: SidebarChromeProps) {
  // Cmd+B / Ctrl+B toggles the rail from page chrome. The same chord is
  // Bold in the email composer, so an editable target keeps the event.
  // Escape still closes an open mobile drawer.
  useEffect(() => {
    if (!onToggleCollapsed && !mobile) return
    const handler = (e: KeyboardEvent) => {
      if (
        onToggleCollapsed &&
        (e.metaKey || e.ctrlKey) &&
        e.key === 'b' &&
        !isEditorShortcutTarget(e.target)
      ) {
        e.preventDefault()
        onToggleCollapsed()
        return
      }
      if (mobile?.open && e.key === 'Escape') {
        mobile.onClose()
      }
    }
    window.addEventListener('keydown', handler)
    return () => window.removeEventListener('keydown', handler)
  }, [onToggleCollapsed, mobile])

  // Body scroll-lock while the mobile drawer is open — same pattern
  // as Headless UI's Dialog. No-op when the consumer didn't enable
  // mobile mode.
  useEffect(() => {
    if (!mobile?.open) return
    const original = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      document.body.style.overflow = original
    }
  }, [mobile?.open])

  const asideBaseClass = cn(
    'flex flex-col transition-all duration-200',
    surfaceClass,
    collapsed ? collapsedWidthClass : expandedWidthClass,
  )

  const asidePositionClass =
    position === 'fixed'
      ? // Fixed: pinned to the viewport edge. Mobile-mode toggles a
        // slide-in transform. z-40 above the backdrop on mobile;
        // z-30 (back to spec) on desktop so it doesn't fight TabBar.
        mobile
          ? cn(
              'fixed inset-y-0 left-0 z-40 transition-transform duration-200 md:z-30 md:translate-x-0',
              mobile.open ? 'translate-x-0' : '-translate-x-full',
            )
          : 'fixed inset-y-0 left-0 z-30'
      : // Relative: aside is positioned by its parent flex layout.
        // Master Quotech HQ uses this because its floating collapse
        // chevron is rendered with `position: absolute` and needs the
        // aside to be its containing block.
        //
        // BUG-2026-05-22-MOBILE-DRAWER-SIDEBAR-OVERFLOW — `h-screen`
        // (100vh) is wrong when this aside is rendered INSIDE a
        // smaller bounded container. The master shell mounts THIS
        // exact SidebarChrome inside `MobileDrawer` on `<lg` — the
        // drawer's content slot is `h-full pt-12` (100vh - 48px =
        // 712px on a 760px viewport), so an `h-screen` child
        // overshoots by exactly 48px and the bottom of the menu
        // (most of the "Hubs" section) sits below the visible
        // viewport. The user can't reach it. Operator complaint
        // 2026-05-22: "why is the menu in burger in the mobile
        // view still like this".
        //
        // `h-full` is the right primitive: it inherits the parent's
        // bounded height. On desktop the parent is DashboardLayout's
        // `<div className="hidden lg:block flex-shrink-0">` inside
        // `<div className="flex h-screen ...">` — flex cross-axis
        // stretch gives it 100vh, identical to the old `h-screen`
        // behaviour. On mobile the parent is MobileDrawer's
        // `flex flex-col h-full pt-12` content slot — `h-full` here
        // gives 712px and the menu fits the viewport.
        'relative h-full z-30'

  return (
    <TooltipProvider delayDuration={150}>
      {/* Mobile backdrop — only rendered when the consumer opted into
          mobile + the drawer is open. Click anywhere outside the
          aside dismisses. `pointer-events-none` while closed so the
          backdrop doesn't swallow desktop clicks. */}
      {mobile && (
        <button
          type="button"
          aria-label="Close navigation"
          aria-hidden={!mobile.open}
          tabIndex={mobile.open ? 0 : -1}
          onClick={mobile.onClose}
          className={cn(
            'fixed inset-0 z-30 bg-black/45 transition-opacity md:hidden',
            mobile.open
              ? 'opacity-100'
              : 'pointer-events-none opacity-0',
          )}
        />
      )}
      <aside className={cn(asideBaseClass, asidePositionClass, className)}>
        {topPortal}
        {header}
        {nav}
        {footer}
        {contextMenu && (
          <ContextMenu
            open={contextMenu.open}
            position={contextMenu.position}
            onClose={contextMenu.onClose}
            items={contextMenu.items}
            width={contextMenu.width ?? 240}
          />
        )}
      </aside>
    </TooltipProvider>
  )
}
